import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { SURVEY_FORM_TOUR_ID, surveyFormTourSteps } from './surveyFormTour';
import { box, input, label, btnNavy, btnPlain, Question, Pick, PickMany } from './surveyFormParts';
import DeskSurveySections from './DeskSurveySections';
import DeskMoveSections from './DeskMoveSections';
import { formatDate } from '../../utils/format';
import {
  STATUS_LABELS, STATUS_TONES, LOCKED_STATUSES, SECTION_LABELS, NETWORKS,
  CAN_DO_LABELS, HOURS_LABELS, VISIT_LABELS, HEIGHT_LABELS, YES_NO_LABELS, LIVE_LABELS,
  SURFACE_LABELS, PERMIT_LABELS, SITE_FLAG_LABELS, STANDARD_ASSUMPTIONS,
  deviations, problemsFor, engineerDays, kindOf, MOVE_DAY_FIELDS,
} from '../../utils/siteSurveys';

// One site survey: the AV / IT Site Survey form, question by question, as the
// engineer fills it in on site (on a phone as often as not) and the PM reviews
// it. src/api/routes/surveys.js decides what may change; the form shows what
// it will refuse before it is asked, such as a submit with questions missing.

const ANSWER_FIELDS = [
  'title', 'location', 'customer_request', 'needed_by', 'site_contact', 'assigned_to', 'visit_date', 'paper_only',
  'can_do', 'can_do_instead', 'engineers', 'days_each', 'hours_window', 'visit_plan', 'visit_plan_reason', 'prerequisites',
  'fixing_surfaces', 'fixing_notes', 'height_access', 'power_ok', 'power_notes', 'network_ok', 'network_notes',
  'area_clear', 'area_notes', 'permits_needed', 'permits', 'site_flags', 'site_notes', 'final_comments',
  'desk_kit_items', 'requirement_version', 'other_surveyors',
  'client_department', 'move_lead', 'move_date', 'move_window', 'change_ref', 'move_outcome', 'evidence_location',
];

/** The rows of one section of the form. `cols` names which of qty, spec and live it takes. */
function Lines({ section, lines, setLines, cols, itemLabel, specLabel, disabled, numbered }) {
  const rows = lines.filter(l => l.section === section);
  const update = (i, k, v) => {
    let n = -1;
    setLines(lines.map(l => (l.section === section && ++n === i ? { ...l, [k]: v } : l)));
  };
  const remove = (i) => {
    let n = -1;
    setLines(lines.filter(l => !(l.section === section && ++n === i)));
  };
  const add = () => setLines([...lines, { section, item: '', qty: '', spec: '', live: null }]);
  return (
    <div>
      {rows.length === 0 && <div className="text-[10px] text-gray-400 mb-1">Nothing recorded yet.</div>}
      {rows.map((l, i) => (
        // On a phone the item takes its own line and the rest sit under it;
        // from sm up the row is one line.
        <div key={i} className="flex flex-wrap sm:flex-nowrap items-start gap-1.5 mb-1.5">
          {numbered && <span className="text-[10px] text-gray-400 pt-2 w-4 shrink-0">{i + 1}.</span>}
          <input className={`${box} min-w-0 flex-1 ${cols.length ? 'basis-full sm:basis-0' : ''}`} value={l.item || ''} disabled={disabled} placeholder={itemLabel} aria-label={itemLabel}
            onChange={e => update(i, 'item', e.target.value)} />
          {cols.includes('qty') && (
            <input className={`${box} w-16 shrink-0`} inputMode="decimal" value={l.qty ?? ''} disabled={disabled} placeholder="Qty" aria-label="Quantity"
              onChange={e => update(i, 'qty', e.target.value)} />
          )}
          {cols.includes('spec') && (
            <input className={`${box} min-w-0 flex-1`} value={l.spec || ''} disabled={disabled} placeholder={specLabel} aria-label={specLabel}
              onChange={e => update(i, 'spec', e.target.value)} />
          )}
          {cols.includes('live') && (
            <select className={`${box} w-28 shrink-0`} value={l.live || ''} disabled={disabled} aria-label="Live" onChange={e => update(i, 'live', e.target.value || null)}>
              <option value="">Live?</option>
              {Object.entries(LIVE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
          {!disabled && <button type="button" onClick={() => remove(i)} className="shrink-0 text-gray-400 hover:text-red-600 text-[14px] px-1 pt-1" aria-label="Remove this row">×</button>}
        </div>
      ))}
      {!disabled && <button type="button" onClick={add} className="text-[10px] font-semibold text-gold hover:underline">+ Add a row</button>}
    </div>
  );
}

function YesNoWithNote({ title, value, note, onValue, onNote, disabled, noteHint }) {
  return (
    <div className="mb-2.5">
      <div className={label}>{title}</div>
      <Pick options={YES_NO_LABELS} value={value} onChange={onValue} disabled={disabled} />
      {value === 'no' && (
        <input className={`${input} mt-1.5`} value={note || ''} disabled={disabled} placeholder={noteHint} aria-label={`${title}: details`} onChange={e => onNote(e.target.value)} />
      )}
    </div>
  );
}

const fromSurvey = (s) => Object.fromEntries(ANSWER_FIELDS.map(k => [k, s[k] ?? (k === 'paper_only' ? 0 : null)]));

// The completed form back into this survey. Ops issued the form, stamped
// with the survey's reference, so a form for another survey is refused here
// rather than read into the wrong one.
function UploadForm({ survey, formName, dirty, dayOpen, onDone, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState(null);
  const run = async () => {
    setBusy(true); setError('');
    try {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error(`Could not read ${file.name}`));
        r.readAsDataURL(file);
      });
      const res = await api.post(`surveys/${survey.id}/upload`, { filename: file.name, content_type: file.type, data });
      onDone(res.survey);
      setNotes(res.notes || []);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={notes ? `${survey.reference} updated from the form` : `Upload the completed form to ${survey.reference}`} onClose={onClose}
      footer={notes ? <button onClick={onClose} className={btnNavy}>Close</button> : <>
        <button onClick={onClose} className={btnPlain}>Close</button>
        <button onClick={run} disabled={busy || !file} className={btnNavy}>{busy ? 'Reading…' : dirty ? 'Upload and replace my changes' : 'Upload'}</button>
      </>}>
      {notes ? (
        <div className="text-[11px] text-gray-700">
          <div className="mb-2">The form was read into {survey.reference} and is kept with it.</div>
          {notes.length ? (
            <>
              <div className="font-semibold text-amber-700 mb-1">Check these, which the form did not answer clearly:</div>
              <ul className="list-disc pl-4 text-[10px] text-amber-700 space-y-0.5">{notes.map(n => <li key={n}>{n}</li>)}</ul>
            </>
          ) : <div className="text-green-700">Everything on the form was read.</div>}
        </div>
      ) : (
        <>
          <div className="text-[10px] text-gray-500 mb-2">
            {dayOpen
              ? `${survey.reference} is reviewed, so only move day is read from the form: each move's status, blocker, checks and kit, matched by Move ID, and the move outcome.`
              : `The ${formName} downloaded from ${survey.reference}, completed. Its answers and tables replace what is here. A question left blank on the form keeps the answer it has here.`}
          </div>
          {dirty && <div className="text-[10px] text-amber-700 mb-2">You have changes here that are not saved. Uploading the form replaces them.</div>}
          <label className={label} htmlFor="up-file">Completed form</label>
          <input id="up-file" type="file" accept=".docx,.xlsx" className="text-[11px]" onChange={e => { setFile(e.target.files?.[0] || null); setError(''); }} />
          {error && <div role="alert" className="text-[10px] text-red-600 mt-2">{error}</div>}
        </>
      )}
    </Modal>
  );
}

export default function SurveyForm({ surveyId, onBack, onChanged }) {
  const [survey, setSurvey] = useState(null);
  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([]);
  const [people, setPeople] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [photoMsg, setPhotoMsg] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [linkRef, setLinkRef] = useState('');
  const [formMsg, setFormMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  // A desk survey's standards, desks and extras (DeskSurveySections).
  const [parts, setPartsState] = useState({ standards: [], desks: [], extras: [], moves: [] });

  const take = useCallback((s) => {
    setSurvey(s);
    setForm(fromSurvey(s));
    setLines((s.lines || []).map(l => ({ ...l, qty: l.qty ?? '' })));
    setPartsState({ standards: s.standards || [], desks: s.desks || [], extras: s.extras || [], moves: s.moves || [] });
    setDirty(false);
  }, []);

  useEffect(() => {
    api.get(`surveys/${surveyId}`).then(take).catch(e => setLoadError(e?.message || 'Could not load the survey'));
    api.get('surveys', { status: 'open' }).then(d => setPeople(d.people || [])).catch(() => {});
  }, [surveyId, take]);

  const locked = survey ? LOCKED_STATUSES.includes(survey.status) : true;
  // Opens once, the first time somebody opens a survey they can still fill in.
  // A submitted survey is still open but is waiting for review, and whoever
  // opens it is reviewing rather than filling in, so it does not open there.
  const tour = useFeatureTour(SURVEY_FORM_TOUR_ID, { enabled: !!survey && !locked && survey.status !== 'submitted' });
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };
  const setRows = (next) => { setLines(next); setDirty(true); };
  const setParts = (next) => { setPartsState(next); setDirty(true); };
  // Room, desk standardisation or desk move. Both desk kinds share the desk layout (no numbered questions).
  const kind = kindOf(survey);
  const isDesks = kind !== 'room';
  const isMoves = kind === 'moves';
  // A reviewed desk move still has move day to record: only those fields stay open.
  const dayOpen = isMoves && survey?.status === 'reviewed';
  const problems = useMemo(() => (form ? problemsFor({ ...form, kind }, { lines, ...parts }) : []), [form, lines, parts, kind]);
  const notStandard = useMemo(() => (form ? deviations(form) : []), [form]);
  const days = form ? engineerDays(form) : null;

  if (loadError) return <div className="text-center py-8 text-[10px] text-red-600">{loadError}</div>;
  if (!survey || !form) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;

  const save = async () => {
    setBusy(true); setMessage(null);
    try {
      const body = { ...form, lines: lines.map(l => ({ ...l, qty: l.qty === '' ? null : l.qty })) };
      if (kind === 'desks') Object.assign(body, { standards: parts.standards, desks: parts.desks.map(({ _key, ...d }) => d), extras: parts.extras });
      if (isMoves) Object.assign(body, { moves: parts.moves, extras: parts.extras });
      const s = dayOpen
        ? await api.put(`surveys/${survey.id}/move-day`, {
          moves: parts.moves.map(m => ({ id: m.id, ...Object.fromEntries(MOVE_DAY_FIELDS.map(k => [k, m[k] ?? null])) })),
          move_outcome: form.move_outcome || null, evidence_location: form.evidence_location || null,
        })
        : await api.put(`surveys/${survey.id}`, body);
      take(s); onChanged?.();
      setMessage({ tone: 'ok', text: 'Saved.' });
      return true;
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
      return false;
    } finally { setBusy(false); }
  };

  const move = async (status, extra = {}) => {
    if (dirty && !locked && !(await save())) return;
    setBusy(true); setMessage(null);
    try {
      const s = await api.post(`surveys/${survey.id}/status`, { status, ...extra });
      take(s); onChanged?.();
      setMessage({ tone: 'ok', text: `${s.reference} is now ${STATUS_LABELS[s.status]}.` });
      return true;
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
      return false;
    } finally { setBusy(false); }
  };

  const linkOpportunity = async () => {
    setBusy(true); setMessage(null);
    try {
      const s = await api.put(`surveys/${survey.id}`, { sow_reference: linkRef });
      take(s); onChanged?.(); setLinkRef('');
      setMessage({ tone: 'ok', text: `Linked to ${s.opportunity_reference}.` });
    } catch (e) { setMessage({ tone: 'error', text: e.message }); } finally { setBusy(false); }
  };

  const addPhotos = async (files) => {
    setPhotoMsg(null);
    for (const file of files) {
      const data = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error(`Could not read ${file.name}`));
        r.readAsDataURL(file);
      }).catch(e => { setPhotoMsg({ tone: 'error', text: e.message }); return null; });
      if (!data) continue;
      try {
        const res = await api.post(`surveys/${survey.id}/photos`, { filename: file.name, content_type: file.type, data });
        setSurvey(s => ({ ...s, photos: res.photos }));
        setPhotoMsg({ tone: 'ok', text: `Added ${file.name}.` });
      } catch (e) { setPhotoMsg({ tone: 'error', text: e.message }); }
    }
  };
  const removePhoto = async (p) => {
    try {
      const res = await api.delete(`surveys/${survey.id}/photos/${p.id}`);
      setSurvey(s => ({ ...s, photos: res.photos }));
      setPhotoMsg({ tone: 'ok', text: `Removed ${p.file_name}.` });
    } catch (e) { setPhotoMsg({ tone: 'error', text: e.message }); }
  };

  // The room survey as the Word form, the desk survey as the workbook, or the
  // file an imported survey came from.
  const download = async (path, fallbackName) => {
    try {
      const blob = await api.getBlob(`surveys/${survey.id}/${path}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fallbackName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setFormMsg('');
    } catch (e) { setFormMsg(e.message); }
  };
  const ref = survey.opportunity_reference || survey.bid_reference || survey.reference;
  const downloadForm = () => (isDesks
    ? download('excel', `${ref} - ${isMoves ? 'Desk Move' : 'Desk Survey'} - ${survey.title}.xlsx`)
    : download('word', `${ref} - Site Survey - ${survey.title}.docx`));
  const formName = isDesks ? 'Excel form' : 'Word form';
  const canUpload = !locked || dayOpen;

  const back = () => {
    if (dirty && !window.confirm('You have changes that are not saved. Leave without saving them?')) return;
    onBack();
  };

  const canSubmit = ['requested', 'booked', 'in_progress'].includes(survey.status);
  const parentText = survey.opportunity_reference
    ? `${survey.opportunity_reference}${survey.opportunity_detail ? ` · ${survey.opportunity_detail}` : ''}`
    : survey.bid_reference ? `Bid ${survey.bid_reference} · ${survey.bid_opportunity || ''}` : '';

  return (
    <div className="fade-in max-w-4xl">
      <div className="flex items-center justify-between mb-2">
        <button onClick={back} className="text-[10px] text-gray-500 hover:text-navy">← Surveys</button>
        <button onClick={tour.start} className="w-[23px] h-[23px] rounded-full border border-gray-200 text-gray-500 hover:text-navy hover:border-navy text-[11px] font-bold" aria-label="Replay the guided tour of the survey form" title="Replay the guided tour of the survey form">?</button>
      </div>

      <div data-tour="survey-header" className="bg-white border border-gray-200 rounded p-3 mb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] font-bold text-gold">{survey.reference}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${STATUS_TONES[survey.status]}`}>{STATUS_LABELS[survey.status]}</span>
              {!!survey.paper_only && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700">Paper only</span>}
            </div>
            <h2 className="text-base font-extrabold text-navy mt-1 break-words">{survey.title}</h2>
            <div className="text-[10px] text-gray-500 mt-0.5 break-words">
              {[parentText, survey.client_name, survey.client_reference].filter(Boolean).join(' · ')}
            </div>
            {survey.status === 'cancelled' && survey.cancelled_reason && <div className="text-[10px] text-gray-500 mt-1">Cancelled: {survey.cancelled_reason}</div>}
            {survey.status === 'reviewed' && <div className="text-[10px] text-green-700 mt-1">Reviewed by {survey.reviewed_by_name || 'a reviewer'} on {formatDate(survey.reviewed_at)}.</div>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap justify-end gap-1">
              <button data-tour="survey-word" onClick={downloadForm} className={btnPlain}
                title={`This survey's ${formName}, filled in so far and stamped with ${survey.reference}. Fill it in on site, or print it where phones are not allowed.`}>
                Download {formName}
              </button>
              {canUpload && (
                <button data-tour="survey-upload" onClick={() => setUploading(true)} className={btnPlain}
                  title={`Read the completed ${formName} back into ${survey.reference}`}>
                  Upload completed form
                </button>
              )}
            </div>
            {formMsg && <div role="alert" className="text-[10px] text-red-600 max-w-[16rem] text-right">{formMsg}</div>}
            {survey.source_name && (
              <button onClick={() => download('source', survey.source_name)} className="text-[9px] text-gray-500 hover:text-navy underline" title="Download the file this survey was last read from: a completed form or the move list">
                File: {survey.source_name}
              </button>
            )}
          </div>
        </div>
        {!survey.opportunity_reference && survey.bid_reference && survey.bid_status === 'Won' && !locked && (
          <div className="mt-2 p-2 rounded bg-amber-50 text-amber-700 text-[10px] flex flex-wrap items-center gap-2">
            <span>The bid is won but raised no opportunity. Link this survey to one:</span>
            <input className={`${box} w-32`} value={linkRef} onChange={e => setLinkRef(e.target.value)} placeholder="AV-403000" aria-label="Opportunity reference" />
            <button onClick={linkOpportunity} disabled={busy || !linkRef.trim()} className={btnPlain}>Link</button>
          </div>
        )}
      </div>

      <Question n={isDesks ? null : 1} title="Job details" tour="survey-job">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div><label className={label} htmlFor="sv-title">{isMoves ? 'Floor or area' : isDesks ? 'Area or floor' : 'Room or area'}</label><input id="sv-title" className={input} value={form.title || ''} disabled={locked} onChange={e => set('title', e.target.value)} /></div>
          <div><label className={label} htmlFor="sv-loc">Location (building, floor, room, desks)</label><input id="sv-loc" className={input} value={form.location || ''} disabled={locked} onChange={e => set('location', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label} htmlFor="sv-req">What the customer has asked for</label><textarea id="sv-req" rows={2} className={input} value={form.customer_request || ''} disabled={locked} onChange={e => set('customer_request', e.target.value)} /></div>
          <div><label className={label} htmlFor="sv-contact">Site contact</label><input id="sv-contact" className={input} value={form.site_contact || ''} disabled={locked} onChange={e => set('site_contact', e.target.value)} /></div>
          <div><label className={label} htmlFor="sv-needed">Needed by</label><input id="sv-needed" type="date" className={input} value={form.needed_by || ''} disabled={locked} onChange={e => set('needed_by', e.target.value || null)} /></div>
          <div>
            <label className={label} htmlFor="sv-who">Survey carried out by</label>
            <select id="sv-who" className={input} value={form.assigned_to || ''} disabled={locked} onChange={e => set('assigned_to', e.target.value || null)}>
              <option value="">Not assigned</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              {form.assigned_to && !people.some(p => p.id === form.assigned_to) && <option value={form.assigned_to}>{survey.assigned_name}</option>}
            </select>
          </div>
          <div><label className={label} htmlFor="sv-visit">Visit date</label><input id="sv-visit" type="date" className={input} value={form.visit_date || ''} disabled={locked} onChange={e => set('visit_date', e.target.value || null)} /></div>
          {isDesks && <div><label className={label} htmlFor="sv-others">Also surveyed by</label><input id="sv-others" className={input} value={form.other_surveyors || ''} disabled={locked} onChange={e => set('other_surveyors', e.target.value)} /></div>}
          {kind === 'desks' && <div><label className={label} htmlFor="sv-reqv">Requirement version surveyed against</label><input id="sv-reqv" className={input} value={form.requirement_version || ''} disabled={locked} onChange={e => set('requirement_version', e.target.value)} /></div>}
          {isMoves && <>
            <div><label className={label} htmlFor="sv-dept">Client or department</label><input id="sv-dept" className={input} value={form.client_department || ''} disabled={locked} onChange={e => set('client_department', e.target.value)} /></div>
            <div><label className={label} htmlFor="sv-lead">Move lead</label><input id="sv-lead" className={input} value={form.move_lead || ''} disabled={locked} onChange={e => set('move_lead', e.target.value)} /></div>
            <div><label className={label} htmlFor="sv-mdate">Planned move date</label><input id="sv-mdate" type="date" className={input} value={form.move_date || ''} disabled={locked} onChange={e => set('move_date', e.target.value || null)} /></div>
            <div><label className={label} htmlFor="sv-mwin">Move window</label><input id="sv-mwin" className={input} value={form.move_window || ''} placeholder="Such as Saturday 08:00 to 16:00" disabled={locked} onChange={e => set('move_window', e.target.value)} /></div>
            <div><label className={label} htmlFor="sv-chg">Change or ticket reference</label><input id="sv-chg" className={input} value={form.change_ref || ''} disabled={locked} onChange={e => set('change_ref', e.target.value)} /></div>
          </>}
          <label className="sm:col-span-2 flex items-start gap-2 text-[10px] text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={!!form.paper_only} disabled={locked} onChange={e => set('paper_only', e.target.checked ? 1 : 0)} />
            <span><b>Paper only.</b> Phones and cameras are not allowed on this site. The engineer takes the printed {formName} from Download {formName}, and afterwards the answers are typed in here, or the completed form is uploaded with Upload completed form. No photos are taken.</span>
          </label>
        </div>
      </Question>

      {isMoves ? (
        <DeskMoveSections survey={form} parts={parts} setParts={setParts} setField={set} locked={locked} dayOpen={dayOpen} />
      ) : isDesks ? (
        <DeskSurveySections survey={form} parts={parts} setParts={setParts} setField={set} locked={locked} />
      ) : (<>
      <Question n={2} title="Can it be done as asked?" tour="survey-can-do">
        <Pick options={CAN_DO_LABELS} value={form.can_do} onChange={v => set('can_do', v)} disabled={locked} />
        {(form.can_do === 'differently' || form.can_do === 'no') && (
          <textarea rows={2} className={`${input} mt-2`} value={form.can_do_instead || ''} disabled={locked} placeholder="What would you do instead?" aria-label="What would you do instead?" onChange={e => set('can_do_instead', e.target.value)} />
        )}
      </Question>

      <Question n={3} title={SECTION_LABELS.task} hint="The physical tasks, in order. This becomes the schedule of work in the quote.">
        <Lines section="task" lines={lines} setLines={setRows} cols={[]} itemLabel="Task" disabled={locked} numbered />
      </Question>

      <Question n={4} title={SECTION_LABELS.network} hint={`Record the port numbers and whether each point is live. Networks seen on our sites: ${NETWORKS.join(', ')}.`}>
        <Lines section="network" lines={lines} setLines={setRows} cols={['spec', 'live']} itemLabel="Network, such as RDN" specLabel="Port numbers" disabled={locked} />
      </Question>

      <Question n={5} title={SECTION_LABELS.current_kit} hint="Quantities and sizes, please.">
        <Lines section="current_kit" lines={lines} setLines={setRows} cols={['qty', 'spec']} itemLabel="Item" specLabel="Size, length or spec" disabled={locked} />
      </Question>

      <Question n={6} title={SECTION_LABELS.needed_kit} hint="Quantities and sizes, please. This is the part that gets priced." tour="survey-kit">
        <Lines section="needed_kit" lines={lines} setLines={setRows} cols={['qty', 'spec']} itemLabel="Item" specLabel="Size, length or spec" disabled={locked} />
      </Question>

      </>)}

      <Question n={isDesks ? null : 7} title="Engineers and time" tour="survey-time">
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div><label className={label} htmlFor="sv-eng">Engineers on site</label><input id="sv-eng" inputMode="numeric" className={input} value={form.engineers ?? ''} disabled={locked} onChange={e => set('engineers', e.target.value === '' ? null : e.target.value)} /></div>
          <div><label className={label} htmlFor="sv-days">Days or shifts each</label><input id="sv-days" inputMode="decimal" className={input} value={form.days_each ?? ''} disabled={locked} onChange={e => set('days_each', e.target.value === '' ? null : e.target.value)} /></div>
        </div>
        {days && <div className="text-[10px] text-gray-500 mb-2">{days} engineer {days === 1 ? 'day' : 'days'} in all.</div>}
        <div className="mb-2"><div className={label}>Hours</div><Pick options={HOURS_LABELS} value={form.hours_window} onChange={v => set('hours_window', v)} disabled={locked} /></div>
        <div className="mb-2">
          <div className={label}>One visit or phased</div>
          <Pick options={VISIT_LABELS} value={form.visit_plan} onChange={v => set('visit_plan', v)} disabled={locked} />
          {form.visit_plan === 'phased' && <input className={`${input} mt-1.5`} value={form.visit_plan_reason || ''} disabled={locked} placeholder="Why phased?" aria-label="Why phased?" onChange={e => set('visit_plan_reason', e.target.value)} />}
        </div>
        <div><label className={label} htmlFor="sv-pre">Needed before we attend (lead times, pre-build, sign-off)</label><input id="sv-pre" className={input} value={form.prerequisites || ''} disabled={locked} onChange={e => set('prerequisites', e.target.value)} /></div>
      </Question>

      <Question n={isDesks ? null : 8} title="Site and access" hint="Leave an answer blank when the standard assumption holds. Anything answered No here changes the price." tour="survey-site">
        <div className="mb-2.5">
          <div className={label}>Wall or ceiling where we are fixing</div>
          <PickMany options={SURFACE_LABELS} value={form.fixing_surfaces} onChange={v => set('fixing_surfaces', v)} disabled={locked} />
          <input className={`${input} mt-1.5`} value={form.fixing_notes || ''} disabled={locked} placeholder="Details, such as false floor or false ceiling" aria-label="Fixing details" onChange={e => set('fixing_notes', e.target.value)} />
        </div>
        <div className="mb-2.5"><div className={label}>Working at height</div><Pick options={HEIGHT_LABELS} value={form.height_access} onChange={v => set('height_access', v)} disabled={locked} /></div>
        <YesNoWithNote title="Power in place?" value={form.power_ok} note={form.power_notes} onValue={v => set('power_ok', v)} onNote={v => set('power_notes', v)} disabled={locked} noteHint="What is needed?" />
        <YesNoWithNote title="Network points in place and live?" value={form.network_ok} note={form.network_notes} onValue={v => set('network_ok', v)} onNote={v => set('network_notes', v)} disabled={locked} noteHint="Which points?" />
        <YesNoWithNote title="Area clear and usable?" value={form.area_clear} note={form.area_notes} onValue={v => set('area_clear', v)} onNote={v => set('area_notes', v)} disabled={locked} noteHint="What is in the way?" />
        <div className="mb-2.5">
          <div className={label}>Permit, escort, ECR or SIA needed?</div>
          <Pick options={YES_NO_LABELS} value={form.permits_needed} onChange={v => { set('permits_needed', v); if (v !== 'yes') set('permits', null); }} disabled={locked} />
          {form.permits_needed === 'yes' && <div className="mt-1.5"><PickMany options={PERMIT_LABELS} value={form.permits} onChange={v => set('permits', v)} disabled={locked} /></div>}
        </div>
        <div>
          <div className={label}>Anything else</div>
          <PickMany options={SITE_FLAG_LABELS} value={form.site_flags} onChange={v => set('site_flags', v)} disabled={locked} />
          <input className={`${input} mt-1.5`} value={form.site_notes || ''} disabled={locked} placeholder="Details" aria-label="Other site details" onChange={e => set('site_notes', e.target.value)} />
        </div>
      </Question>

      {!isDesks && (
      <Question n={9} title={SECTION_LABELS.risk} hint="Include anything someone else must do first.">
          <Lines section="risk" lines={lines} setLines={setRows} cols={[]} itemLabel="What could stop, delay or add cost" disabled={locked} numbered />
        </Question>
      )}

      {!form.paper_only && (
        <Question title={`Photos (${survey.photos?.length || 0})`} tour="survey-photos">
          <div className="flex flex-wrap gap-2 mb-2">
            {(survey.photos || []).map(p => (
              <figure key={p.id} className="w-28">
                <a href={`/api/surveys/${survey.id}/photos/${p.id}`} target="_blank" rel="noreferrer">
                  <img src={`/api/surveys/${survey.id}/photos/${p.id}`} alt={p.caption || p.file_name} className="w-28 h-20 object-cover rounded border border-gray-200" loading="lazy" />
                </a>
                <figcaption className="text-[9px] text-gray-500 truncate" title={p.file_name}>{p.caption || p.file_name}</figcaption>
                {!locked && <button onClick={() => removePhoto(p)} className="text-[9px] text-red-600 hover:underline">Remove</button>}
              </figure>
            ))}
          </div>
          {!locked && (
            <label className={`${btnPlain} inline-block cursor-pointer`}>
              Add photos
              <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => { addPhotos([...e.target.files]); e.target.value = ''; }} />
            </label>
          )}
          {photoMsg && <div role="status" className={`mt-1.5 text-[10px] ${photoMsg.tone === 'error' ? 'text-red-600' : 'text-green-700'}`}>{photoMsg.text}</div>}
        </Question>
      )}

      <Question title="Sign-off and comments">
        <textarea rows={2} className={input} value={form.final_comments || ''} disabled={locked} placeholder="Final comments, including any items not agreed" aria-label="Final comments" onChange={e => set('final_comments', e.target.value)} />
        {survey.submitted_at && <div className="text-[10px] text-gray-500 mt-1.5">Submitted by {survey.submitted_by_name || 'the engineer'} on {formatDate(survey.submitted_at)}.</div>}
      </Question>

      <section data-tour="survey-standard" className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
        <h3 className="text-[11px] font-bold text-navy mb-1">Not standard ({notStandard.length})</h3>
        {notStandard.length
          ? <ul className="list-disc pl-4 text-[10px] text-amber-700 mb-2">{notStandard.map(d => <li key={d}>{d}</li>)}</ul>
          : <div className="text-[10px] text-gray-500 mb-2">Nothing recorded differs from the standard assumptions.</div>}
        <div className="text-[9px] font-semibold text-gray-500 uppercase mb-0.5">Standard assumptions</div>
        <ul className="list-disc pl-4 text-[10px] text-gray-500">{STANDARD_ASSUMPTIONS.map(a => <li key={a}>{a}</li>)}</ul>
      </section>

      {/* The bar the person acts from, with its result beside the buttons. */}
      <div data-tour="survey-actions" className="sticky bottom-0 z-20 bg-white border border-gray-200 rounded px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {(!locked || dayOpen) && <button onClick={save} disabled={busy || !dirty} className={btnPlain}>{dirty ? (dayOpen ? 'Save move day' : 'Save') : 'Saved'}</button>}
          {canSubmit && (
            <button onClick={() => move('submitted')} disabled={busy || problems.length > 0} className={btnNavy}
              title={problems.length ? problems.join(' ') : 'Send the survey to the PM for review'}>Submit for review</button>
          )}
          {survey.status === 'requested' && form.assigned_to && form.visit_date && <button onClick={() => move('booked')} disabled={busy} className={btnPlain}>Mark booked</button>}
          {survey.status === 'submitted' && survey.can_review && <button onClick={() => move('reviewed')} disabled={busy} className={btnNavy}>Mark reviewed</button>}
          {survey.status === 'submitted' && survey.can_review && <button onClick={() => move('in_progress')} disabled={busy} className={btnPlain}>Send back</button>}
          {survey.status === 'reviewed' && survey.can_review && <button onClick={() => move('in_progress')} disabled={busy} className={btnPlain}>Reopen</button>}
          {!locked && <button onClick={() => setCancelling(true)} disabled={busy} className={`${btnPlain} ml-auto`}>Cancel survey</button>}
          <div role="status" className="w-full text-[10px]">
            {message && <span className={message.tone === 'error' ? 'text-red-600' : 'text-green-700'}>{message.text}</span>}
            {!message && canSubmit && problems.length > 0 && <span className="text-gray-500">To submit: {problems.join(' ')}</span>}
          </div>
        </div>
      </div>

      {cancelling && (
        <Modal title={`Cancel ${survey.reference}?`} onClose={() => setCancelling(false)}
          footer={<>
            <button onClick={() => setCancelling(false)} className={btnPlain}>Keep the survey</button>
            <button onClick={async () => { if (await move('cancelled', { reason: cancelReason })) setCancelling(false); }} disabled={busy || !cancelReason.trim()} className={btnNavy}>Cancel survey</button>
          </>}>
          <div className="text-[11px] text-gray-700 mb-2">A cancelled survey stays on the record with its answers and the reason, and cannot be changed.</div>
          <label className={label} htmlFor="sv-cancel">Why is it cancelled?</label>
          <input id="sv-cancel" className={input} value={cancelReason} onChange={e => setCancelReason(e.target.value)} autoFocus />
          {message?.tone === 'error' && <div className="text-[10px] text-red-600 mt-1.5">{message.text}</div>}
        </Modal>
      )}
      {uploading && (
        <UploadForm survey={survey} formName={formName} dirty={dirty} dayOpen={dayOpen}
          onDone={(s) => { take(s); onChanged?.(); }} onClose={() => setUploading(false)} />
      )}
      {tour.open && (
        <FeatureTour steps={surveyFormTourSteps} context={{ kind, isDesks, photos: !form.paper_only, canReview: !!survey.can_review }}
          onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}
